import React, { useState, useEffect } from 'react';
import Card from './Card';
import StatusIndicator from './StatusIndicator';

const GameBoard = ({ 
  gameState, 
  playerId, 
  onPlayCards, 
  onDrawCard, 
  onCallLastCard, 
  onSelectSuit,
  connectionStatus 
}) => {
  const [selectedCards, setSelectedCards] = useState([]);
  const [showSuitSelector, setShowSuitSelector] = useState(false);
  const [lastPlayedCards, setLastPlayedCards] = useState([]);
  const [skipModal, setSkipModal] = useState(false);
  const [skipMessage, setSkipMessage] = useState("");

  const currentPlayer = gameState.players.find(p => p.id === playerId);
  const opponent = gameState.players.find(p => p.id !== playerId);
  const isMyTurn = gameState.current_player_index === gameState.players.findIndex(p => p.id === playerId);

  // Reset selections when it's not your turn
  useEffect(() => {
    if (!isMyTurn) {
      setSelectedCards([]);
      setShowSuitSelector(false);
    }
  }, [isMyTurn]);

  // Detect skip turn (2, 7, 8, or Joker played)
  useEffect(() => {
    if (!isMyTurn && gameState.last_action) {
      const action = gameState.last_action;
      
      if (action.type === 'skip') {
        setSkipMessage('Your turn was skipped!');
        setSkipModal(true);
        setTimeout(() => {
          setSkipModal(false);
        }, 2000);
      } else if (action.type === 'two_draw' && action.affected_player === playerId) {
        setSkipMessage('2 played! You drew 2 cards and turn returns to opponent!');
        setSkipModal(true);
        setTimeout(() => {
          setSkipModal(false);
        }, 3000);
      } else if (action.type === 'seven_skip' && action.affected_player === playerId) {
        setSkipMessage('7 played! Your turn is skipped!');
        setSkipModal(true);
        setTimeout(() => {
          setSkipModal(false);
        }, 2500);
      } else if (action.type === 'eight_reverse' && action.affected_player === playerId) {
        setSkipMessage('8 played! Turn is reversed back to opponent!');
        setSkipModal(true);
        setTimeout(() => {
          setSkipModal(false);
        }, 2500);
      } else if (action.type === 'joker_skip' && action.affected_player === playerId) {
        setSkipMessage('Joker played! You drew 5 cards and your turn is skipped!');
        setSkipModal(true);
        setTimeout(() => {
          setSkipModal(false);
        }, 3000);
      } else if (action.type === 'jack_suit_selected' && action.player_id !== playerId) {
        const suitSymbols = {
          'hearts': '♥',
          'diamonds': '♦', 
          'clubs': '♣',
          'spades': '♠'
        };
        const suitSymbol = suitSymbols[action.suit] || action.suit.toUpperCase();
        setSkipMessage(`Jack played! Suit changed to ${action.suit.toUpperCase()} ${suitSymbol}`);
        setSkipModal(true);
        setTimeout(() => {
          setSkipModal(false);
        }, 3000);
      }
    }
  }, [gameState.last_action, isMyTurn, playerId]);

  // Show suit selector when Jack is played and it's your turn
  useEffect(() => {
    if (isMyTurn && gameState.last_action && gameState.last_action.type === 'jack_played') {
      setShowSuitSelector(true);
    }
  }, [gameState.last_action, isMyTurn]);

  const handleCardClick = (card) => {
    if (!isMyTurn) return;

    const isSelected = selectedCards.some(c => 
      c.rank === card.rank && c.suit === card.suit
    );

    if (isSelected) {
      setSelectedCards(selectedCards.filter(c => 
        !(c.rank === card.rank && c.suit === card.suit)
      ));
    } else {
      // Check if we can add this card to selection
      if (selectedCards.length === 0 || card.rank === selectedCards[0].rank) {
        setSelectedCards([...selectedCards, card]);
      }
    }
  };



  const handlePlayCards = () => {
    if (selectedCards.length > 0) {
      onPlayCards(selectedCards);
      setSelectedCards([]);
    }
  };

  const handleDrawCard = () => {
    onDrawCard();
  };

  const handleCallLastCard = () => {
    onCallLastCard();
  };

  const handleSuitSelect = (suit) => {
    onSelectSuit(suit);
    setShowSuitSelector(false);
  };

  const getValidPlays = () => {
    if (!currentPlayer || !gameState.top_card) return [];
    
    return currentPlayer.hand.filter(card => {
      // Jokers can always be played
      if (card.rank === 'JOKER') return true;
      
      // Jacks can always be played (wild card)
      if (card.rank === 'J') return true;
      
      // Any card can be played on top of Jokers
      if (gameState.top_card.rank === 'JOKER') {
        return true;
      }
      
      // Check if there's a current_suit set (from Jack wild card)
      if (gameState.current_suit) {
        // Only check if card matches the current_suit
        return card.suit === gameState.current_suit;
      }
      
      // Regular cards must match suit or rank
      return card.suit === gameState.top_card.suit || 
             card.rank === gameState.top_card.rank;
    });
  };

  const validPlays = getValidPlays();
  const canPlayCards = selectedCards.length > 0 && 
    selectedCards.every(card => validPlays.some(vp => 
      vp.rank === card.rank && vp.suit === card.suit
    ));

  // Draw penalty logic
  const pendingDraw = gameState.pending_draw_count > 0;
  const isPenaltyTurn = pendingDraw && isMyTurn;
  const penaltyRank = gameState.pending_draw_rank;
  const penaltyCount = gameState.pending_draw_count;

  // Only allow playing matching cards or drawing if penalty is active
  const canStackPenalty = isPenaltyTurn && selectedCards.length > 0 && selectedCards.every(card => card.rank === penaltyRank);
  const canDrawPenalty = isPenaltyTurn && selectedCards.length === 0;

  return (
    <div className="min-h-screen bg-gray-900 p-4 pb-24">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-bold text-white">🃏 Last Card</h1>
            <StatusIndicator status={connectionStatus} />
          </div>
          <div className="text-right">
            <div className="text-white font-semibold">
              {gameState.winner ? 'Game Over!' : isMyTurn ? 'Your Turn' : 'Opponent\'s Turn'}
            </div>
            {gameState.winner && (
              <div className="text-lg font-bold text-yellow-400">
                {gameState.winner === playerId ? 'You Won! 🎉' : 'You Lost 😢'}
              </div>
            )}
          </div>
        </div>

        {/* Draw Penalty Counter */}
        {pendingDraw && (
          <div className="mb-4 text-center">
            <div className="inline-block bg-red-700 text-white px-6 py-2 rounded-xl text-lg font-bold shadow-lg animate-pulse">
              Draw Penalty: {penaltyCount} card{penaltyCount > 1 ? 's' : ''} ({penaltyRank === '2' ? '2' : 'Joker'})
            </div>
            {isPenaltyTurn && (
              <div className="mt-2 text-yellow-300 font-semibold">You must play another {penaltyRank} or draw {penaltyCount} cards.</div>
            )}
          </div>
        )}

        {/* Score Display */}
        <div className="bg-gray-800 rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="text-white">
              <span className="font-semibold">{currentPlayer?.name || 'You'}</span>
              <span className="text-gray-400 ml-2">Score: {currentPlayer?.score || 0}</span>
            </div>
            <div className="text-white">
              <span className="font-semibold">{opponent?.name || 'Opponent'}</span>
              <span className="text-gray-400 ml-2">Score: {opponent?.score || 0}</span>
            </div>
          </div>
        </div>

        {/* Game Board */}
        <div className="game-board">
          {/* Opponent Info */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-white font-semibold">
                {opponent?.name || 'Opponent'}
              </h3>
              <div className="text-white">
                Cards: {opponent?.hand?.length || 0}
              </div>
            </div>
            <div className="opponent-hand">
              {opponent?.hand?.map((_, index) => (
                <Card key={index} back={true} />
              ))}
            </div>
          </div>

          {/* Game Center - Deck and Discard Pile */}
          <div className="flex justify-center items-center space-x-8 mb-6">
            {/* Deck */}
            <div className="deck-container">
              <div className="deck relative">
                <Card back={true} />
                <div className="deck-count">
                  {gameState.deck?.length || 0}
                </div>
              </div>
            </div>

            {/* Discard Pile */}
            <div className="discard-pile">
              {gameState.top_card ? (
                <Card card={gameState.top_card} disabled={true} />
              ) : (
                <div className="text-gray-500 text-center">
                  <div className="text-2xl mb-2">🃏</div>
                  <div className="text-sm">No cards played yet</div>
                </div>
              )}
            </div>
          </div>

          {/* Current Suit Display */}
          {gameState.current_suit && (
            <div className="text-center mb-4">
              <div className="inline-block bg-blue-600 text-white px-4 py-2 rounded-lg">
                Current Suit: {gameState.current_suit.toUpperCase()}
              </div>
            </div>
          )}

          {/* Game Controls - Moved to bottom */}
          {!gameState.winner && (
            <div className="fixed bottom-0 left-0 right-0 bg-gray-800 border-t border-gray-700 p-4">
              <div className="max-w-6xl mx-auto flex justify-center space-x-4">
                {/* Penalty draw/stack controls */}
                {isPenaltyTurn ? (
                  <>
                    <button
                      onClick={handlePlayCards}
                      disabled={!canStackPenalty}
                      className="btn btn-danger text-lg py-3 px-6"
                    >
                      Play {penaltyRank}{selectedCards.length > 1 ? ` x${selectedCards.length}` : ''}
                    </button>
                    <button
                      onClick={handleDrawCard}
                      disabled={!canDrawPenalty}
                      className="btn btn-primary text-lg py-3 px-6"
                    >
                      Draw {penaltyCount} Card{penaltyCount > 1 ? 's' : ''}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handlePlayCards}
                      disabled={!canPlayCards || !isMyTurn}
                      className="btn btn-success text-lg py-3 px-6"
                    >
                      Play Cards ({selectedCards.length})
                    </button>
                    <button
                      onClick={handleDrawCard}
                      disabled={!isMyTurn}
                      className="btn btn-primary text-lg py-3 px-6"
                    >
                      Draw Card
                    </button>
                  </>
                )}
                {currentPlayer?.hand?.length === 1 && !currentPlayer.has_called_last_card && !isPenaltyTurn && (
                  <button
                    onClick={handleCallLastCard}
                    disabled={!isMyTurn}
                    className="btn btn-danger text-lg py-3 px-6"
                  >
                    Call Last Card!
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Suit Selector */}
          {showSuitSelector && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-gray-800 p-6 rounded-xl">
                <h3 className="text-white text-lg font-semibold mb-4 text-center">
                  Choose a Suit
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  {[
                    { name: 'Hearts', symbol: '♥', value: 'hearts', color: 'text-red-500' },
                    { name: 'Diamonds', symbol: '♦', value: 'diamonds', color: 'text-red-500' },
                    { name: 'Clubs', symbol: '♣', value: 'clubs', color: 'text-black' },
                    { name: 'Spades', symbol: '♠', value: 'spades', color: 'text-black' }
                  ].map(suit => (
                    <button
                      key={suit.value}
                      onClick={() => handleSuitSelect(suit.value)}
                      className="btn btn-primary text-lg py-4 px-6 flex items-center justify-center space-x-3"
                    >
                      <span className={`text-2xl ${suit.color}`}>{suit.symbol}</span>
                      <span>{suit.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Skip Turn Modal */}
          {skipModal && (
            <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
              <div className="bg-gray-900 text-white p-8 rounded-xl shadow-lg text-2xl font-bold">
                {skipMessage}
              </div>
            </div>
          )}

          {/* Player Hand */}
          <div className="mt-6 mb-24"> {/* Added bottom margin for fixed controls */}
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white font-semibold">
                Your Hand ({currentPlayer?.hand?.length || 0} cards)
              </h3>
              {currentPlayer?.has_called_last_card && (
                <div className="bg-yellow-600 text-white px-3 py-1 rounded-lg text-sm font-semibold">
                  Last Card Called!
                </div>
              )}
            </div>
            
            <div className="player-hand">
              {currentPlayer?.hand?.map((card, index) => (
                                  <Card
                    key={`${card.rank}-${card.suit}-${index}`}
                    card={card}
                    isSelected={selectedCards.some(c => 
                      c.rank === card.rank && c.suit === card.suit
                    )}
                    onClick={handleCardClick}
                    disabled={!isMyTurn}
                  />
              ))}
            </div>
          </div>
        </div>

        {/* Game Info */}
        <div className="mt-6 bg-gray-800 rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4 text-sm text-gray-300">
            <div>
              <strong>Deck:</strong> {gameState.deck?.length || 0} cards
            </div>
            <div>
              <strong>Discard Pile:</strong> {gameState.discard_pile?.length || 0} cards
            </div>
          </div>
          
          {/* New Game Button */}
          {gameState.winner && (
            <div className="mt-4 text-center">
              <button
                onClick={() => window.location.reload()}
                className="btn btn-primary"
              >
                🎮 New Game
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameBoard; 